import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import { ButtonOutline } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import styled, { css } from 'styled-components';

import CtaIcon from '../assets/cta-icon.svg';

import { RedLinkStyle, Section } from './AboutStudySlice';
import { Description as DescriptionWrapper } from './BenefitsSlice';

import { fieldHasContent, transformProperties } from '@/src/domains/prismic/helpers';
import { tabletBreakpoint, fromTabletBreakpoint } from '@/src/domains/shared/breakpoints';
import QuoteIcon from '@/src/views/GlobalBenefitsReport/assets/quote-icon.svg';
import { Label, Title } from '@/src/views/GlobalBenefitsReport/slices/BenefitsOverviewSlice';
import {
  Index,
  Percentage,
  BlockTitle,
} from '@/src/views/GlobalBenefitsReport/slices/WorkBenefitSlice';

export const SectionWrapper = styled(Section)`
  && {
    background-color: #fff5f5;
    color: var(--primaryColor);
    text-align: left;

    ${BlockTitle} {
      margin-bottom: 20px;
      font-family: var(--typography-fontBossa);
    }

    ${Title} {
      ${({ theme }) => theme.typography.scale4};
      font-weight: 500;
      color: var(--primaryColor);
    }

    ${({ variant }) => {
      switch (variant) {
        case 'with_quote':
          return css`
            background-color: var(--colors-blank);

            ${Title} {
              max-width: 725px;
              margin: 0 auto;
              text-align: center;
            }
          `;
        default:
          return css``;
      }
    }}
  }
`;

const BenefitBlock = styled.div`
  padding: 40px 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:not(:last-child) {
    border-bottom: 1px solid #ffcecb;
  }

  ${tabletBreakpoint(css`
    flex-direction: column;
    align-items: flex-start;
  `)}

  ${({ variant }) => {
    switch (variant) {
      case 'with_quote':
        return css`
          flex-direction: row-reverse;
        `;
      default:
        return css``;
    }
  }}
`;

export const TitleWrapper = styled(Title)`
  && {
    margin-bottom: 50px;
  }
`;

export const IndexWrapper = styled(Index)`
  && {
    color: var(--secondaryColor);
  }
`;

export const Description = styled.div`
  ${({ theme }) => theme.typography.titleSM};
  font-weight: 400;
  max-width: 700px;
  ${RedLinkStyle}
`;

const SecondParagraph = styled.div`
  display: flex;
  align-items: center;

  ${tabletBreakpoint(css`
    flex-direction: column;
    align-items: flex-start;
  `)}
`;

const PercentageWrapper = styled(Percentage)`
  && {
    ${tabletBreakpoint(css`
      margin-top: 20px;
    `)}
  }
`;

const SecondPercentageWrapper = styled(PercentageWrapper)`
  margin-left: 10px;

  ${tabletBreakpoint(css`
    margin-left: 20px;
  `)}
`;

const PercentageBlock = styled.div`
  display: flex;
`;

const PercentageNumber = styled.div`
  color: var(--secondaryColor);
  margin-right: 20px;
  margin-bottom: 6px;
`;

const PercentageLabel = styled.div`
  font-size: 16px;
  color: var(--secondaryColor);
`;

const BlockContent = styled.div`
  width: 65%;
  max-width: 565px;

  ${tabletBreakpoint(css`
    width: 95%;
    max-width: unset;
  `)}
`;

const IconWrapper = styled.div`
  margin-bottom: 40px;
  path {
    fill: #cd324f;
  }
`;

const BlockIllustration = styled.div`
  ${tabletBreakpoint(css`
    padding: 30px 0;
  `)}
`;

const QuoteName = styled.div`
  font-family: var(--typography-fontBossa);
  color: #cd324f;
  ${({ theme }) => theme.typography.titleSM};

  > * {
    margin-bottom: 0;
  }
`;

const QuoteDescription = styled.div`
  padding-bottom: 40px;
  opacity: 0.9;
  ${({ theme }) => theme.typography.bodyLead};

  p {
    line-height: 1.5;
  }
`;

const QuoteImage = styled.div`
  margin-right: 20px;
`;

const QuoteContent = styled.div`
  display: flex;

  ${tabletBreakpoint(css`
    flex-direction: column;
  `)}
`;

const Quote = styled.div``;

const QuotePosition = styled.div``;

const QuoteBlock = styled.div`
  ${({ variant }) => {
    switch (variant) {
      case 'with_quote':
        return css`
          padding: 28px 40px 40px;
          background: var(--primaryBgColor);
          border-radius: 0px 20px;
        `;
      case 'with_quote_and_block':
        return css`
          display: flex;
          align-items: center;
          justify-content: space-between;

          ${fromTabletBreakpoint(css`
            ${Quote} {
              width: 70%;
              max-width: 587px;
            }

            ${QuotePosition} {
              max-width: 199px;
            }

            ${QuoteContent} {
              align-items: center;
            }
          `)}

          ${tabletBreakpoint(css`
            flex-direction: column;
            align-items: flex-start;
          `)}
        `;
      default:
        return css``;
    }
  }}
`;

const CtaTitle = styled.div`
  ${({ theme }) => theme.typography.titleSM};
  font-weight: 500;
`;

const CtaDescription = styled(DescriptionWrapper)`
  line-height: 1.5;
  margin-bottom: 22px;
`;

const CtaImage = styled.div`
  width: 40%;
`;

const CtaContent = styled.div`
  width: 56%;
`;

const CtaBlock = styled.div`
  padding: 40px 53px;
  background: var(--colors-blank);
  border-radius: 0px 20px;
  display: flex;
  justify-content: space-between;

  ${tabletBreakpoint(css`
    flex-direction: column;
    align-items: flex-start;
    margin-top: 30px;

    ${CtaImage}, ${CtaContent} {
      width: 100%;
      margin-top: 20px;
    }
  `)}
`;

const CtaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const CtaButton = styled(ButtonOutline)`
  && {
    && {
      background-color: transparent;
      padding: 0;
      border: none;
      color: #e03d5c;
      font-family: var(--typography-fontBossa);
      margin-bottom: 14px;

      svg {
        margin-left: 8px;
      }
    }
  }
`;

export function PercentageWithDescription({
  percentage,
  percentageDescription,
  percentageLabel,
  secondPercentage,
  secondPercentageLabel,
}) {
  return (
    <SecondParagraph>
      <PercentageBlock>
        {fieldHasContent(percentage) && (
          <PercentageWrapper>
            <PercentageNumber>{percentage}</PercentageNumber>
            {fieldHasContent(percentageLabel) && (
              <PercentageLabel>{percentageLabel}</PercentageLabel>
            )}
          </PercentageWrapper>
        )}
        {fieldHasContent(secondPercentage) && (
          <SecondPercentageWrapper>
            <PercentageNumber>{secondPercentage}</PercentageNumber>
            {fieldHasContent(secondPercentageLabel) && (
              <PercentageLabel>{secondPercentageLabel}</PercentageLabel>
            )}
          </SecondPercentageWrapper>
        )}
      </PercentageBlock>
      {fieldHasContent(percentageDescription) && (
        <Description>
          <PrismicRichText field={percentageDescription} />
        </Description>
      )}
    </SecondParagraph>
  );
}

export function OverviewSlice({ slice }) {
  const { primary, fields } = transformProperties(slice);
  const {
    variant,
    label,
    title,
    quoteImage,
    quote,
    quoteName,
    quotePosition,
    ctaBlockImage,
    ctaBlockTitle,
    ctaBlockDescription,
    firstLinkLabel,
    secondLinkLabel,
    thirdLinkLabel,
    firstLink,
    secondLink,
    thirdLink,
  } = primary;

  const ctaLinks = [
    { ctaLabel: firstLinkLabel, link: firstLink },
    { ctaLabel: secondLinkLabel, link: secondLink },
    { ctaLabel: thirdLinkLabel, link: thirdLink },
  ];

  return (
    <SectionWrapper variant={variant}>
      <ContentContainer>
        {fieldHasContent(label) && <Label>{label}</Label>}
        {fieldHasContent(title) && (
          <TitleWrapper>
            <PrismicRichText field={title} />
          </TitleWrapper>
        )}
        <div>
          {fields.map(
            (
              {
                blockTitle,
                blockIllustration,
                blockPercentage,
                blockPercentageLabel,
                blockSecondPercentage,
                blockSecondPercentageLabel,
                firstDescription,
                secondDescription,
              },
              index
            ) => (
              <BenefitBlock variant={variant} key={index}>
                <BlockContent>
                  <IndexWrapper>{(index + 1).toString().padStart(2, '0')}</IndexWrapper>
                  {fieldHasContent(blockTitle) && (
                    <BlockTitle>
                      <PrismicRichText field={blockTitle} />
                    </BlockTitle>
                  )}

                  {blockPercentage && secondDescription && (
                    <PercentageWithDescription
                      percentage={blockPercentage}
                      percentageDescription={firstDescription}
                      percentageLabel={blockPercentageLabel}
                      secondPercentage={blockSecondPercentage}
                      secondPercentageLabel={blockSecondPercentageLabel}
                    />
                  )}
                  {fieldHasContent(secondDescription) && (
                    <Description>
                      <PrismicRichText field={secondDescription} />
                    </Description>
                  )}
                </BlockContent>
                <BlockIllustration>
                  {blockIllustration && (
                    <div>
                      <Image
                        src={blockIllustration?.url}
                        alt={blockIllustration?.alt || ''}
                        width={blockIllustration?.dimensions.width}
                        height={blockIllustration?.dimensions.height}
                        quality="100"
                      />
                    </div>
                  )}
                </BlockIllustration>
              </BenefitBlock>
            )
          )}
        </div>
        {fieldHasContent(quote) && (
          <QuoteBlock variant={variant}>
            <Quote>
              <IconWrapper>
                <QuoteIcon />
              </IconWrapper>
              <QuoteDescription>
                <PrismicRichText field={quote} />
              </QuoteDescription>
            </Quote>
            <QuoteContent>
              {quoteImage && (
                <QuoteImage>
                  <Image
                    src={quoteImage?.url}
                    alt={quoteImage?.alt || ''}
                    width={quoteImage?.dimensions.width}
                    height={quoteImage?.dimensions.height}
                    quality="100"
                  />
                </QuoteImage>
              )}
              <div>
                {fieldHasContent(quoteName) && (
                  <QuoteName>
                    <PrismicRichText field={quoteName} />
                  </QuoteName>
                )}
                {fieldHasContent(quoteName) && (
                  <QuotePosition>
                    <PrismicRichText field={quotePosition} />
                  </QuotePosition>
                )}
              </div>
            </QuoteContent>
          </QuoteBlock>
        )}

        {fieldHasContent(ctaBlockTitle) && (
          <CtaBlock>
            {ctaBlockImage && (
              <CtaImage>
                <Image
                  src={ctaBlockImage?.url}
                  alt={ctaBlockImage?.alt || ''}
                  width={ctaBlockImage?.dimensions.width}
                  height={ctaBlockImage?.dimensions.height}
                  quality="100"
                />
              </CtaImage>
            )}
            <CtaContent>
              {fieldHasContent(ctaBlockTitle) && (
                <CtaTitle>
                  <PrismicRichText field={ctaBlockTitle} />
                </CtaTitle>
              )}
              {fieldHasContent(ctaBlockDescription) && (
                <CtaDescription>
                  <PrismicRichText field={ctaBlockDescription} />
                </CtaDescription>
              )}
              <CtaWrapper>
                {ctaLinks.map(({ ctaLabel, link }, index) => (
                  <CtaButton key={index} href={link?.url} target={link?.target}>
                    {ctaLabel} <CtaIcon />
                  </CtaButton>
                ))}
              </CtaWrapper>
            </CtaContent>
          </CtaBlock>
        )}
      </ContentContainer>
    </SectionWrapper>
  );
}
